import { Stack, TextField, IconButton, Button, DialogActions, DialogContent, DialogTitle, Dialog, styled, Card, CardContent, Typography, LinearProgress, Box, Link, SnackbarCloseReason, Snackbar } from "@mui/material"
import { blue } from "@mui/material/colors"
import SearchIcon from "@mui/icons-material/Search";
import { FormEvent, Fragment, useEffect, useState } from "react";
import RiskScorePanel, { RiskScoreAnalysis } from "./RiskScorePanel";
import CloseIcon from '@mui/icons-material/Close';
import React from "react";
import ArticleTitle from "./ArticleTitle";
import { auth } from "../firebase/firebase";
import { argosApiEntryPoint } from "../GlobalConfig";
import { OpenAlexReferenceData } from "./CommonInterfaces";


function RiskScore() {

  const [doiSearchQuery, setDoiSearchQuery] = useState('');
  const [doiNotFound, setDoiNotFound] = useState(false);
  const [invalidDoi, setInvalidDoi] = useState(false);
  const [inspireMe, setInspireMe] = useState(false);
  const [doiSearchError, setDoiSearchError] = useState(false);
  const [doiSearchResult, setDoiSearchResult] = useState<RiskScoreAnalysis>();
  const [problematicReferences, setProblematicReferences] = useState<OpenAlexReferenceData[]>([])
  const [open, setOpen] = useState(false);
  const [searchInProgress, setSearchInProgress] = useState(false)
  const [openError, setOpenError] = useState(false);

  const handleCloseError = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpenError(false);
  };


  const handleClose = () => {
    setOpen(false);
  };

  
  const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
      padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
      padding: theme.spacing(1),
    },
  })); 

  async function fetchDataFromAPI(url: string): Promise<OpenAlexReferenceData> {
    let authToken = await auth.currentUser?.getIdToken(true)

    return fetch(url, {
      method: 'GET',
      headers: {
        'content-type': 'application/json;charset=UTF-8',
        'Authorization': 'Bearer ' + authToken
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      });
  }

  function convertInputToValidDoi() {
    let processedDoi = doiSearchQuery
      .replace("http:\/\/", "https:\/\/")
      .replace("dx.doi.org", "doi.org")

    if (!processedDoi.startsWith("https://")) {
      if (processedDoi.charAt(0) === '/') {
        processedDoi = "https://doi.org" + processedDoi
      } else if (firstCharacterIsNumber(processedDoi)) {
        processedDoi = "https://doi.org/" + processedDoi
      } else if (processedDoi.startsWith("doi:")) {
        processedDoi = "https://doi.org/" + processedDoi.substring(4)
      } else {
        setInvalidDoi(true)
      }
    }
    return processedDoi
  }

  function firstCharacterIsNumber(processedDoi: string) {
    return processedDoi.charAt(0) >= '0' && processedDoi.charAt(0) <= '9'
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setDoiNotFound(false)
    setDoiSearchError(false)
    setInvalidDoi(false)

    let validdDoi = convertInputToValidDoi()

    if (!invalidDoi) {
      setSearchInProgress(true)
      let authToken = await auth.currentUser?.getIdToken(true)

      const response = await fetch(argosApiEntryPoint + '/risk-score?doi=' + validdDoi, {
        method: 'GET',
        headers: {
          'content-type': 'application/json;charset=UTF-8',
          'Authorization': 'Bearer ' + authToken
        }
      });
      if (response.ok) {
        const riskScoreAnalysis: RiskScoreAnalysis = await response.json();

        const problematicStuff = riskScoreAnalysis.referencesAnalysis.filter(ref => ref.problemsFound)

        // TODO: Remove runtime dependency from OpenAlex
        setProblematicReferences([])

        if (problematicStuff.length === 0) {
          setDoiSearchResult(riskScoreAnalysis)
          setOpen(true)
        } else {
          problematicStuff.map(async (p, i) => {
            setTimeout(function () {
              fetchDataFromAPI(
                argosApiEntryPoint + '/tmp/references?id=' + p.referenceOpenAlexId
              ).then(results => {
                setProblematicReferences((prev) => [...prev, results])
                if (i === (problematicStuff.length - 1)) {
                  setDoiSearchResult(riskScoreAnalysis)
                  setOpen(true)
                }
              }
              ).catch(err => {
                setOpenError(true)
              });
            }, 250 * i);
          })
        }

      } else if (response.status === 404) {
        setDoiNotFound(true)
      } else {
        setDoiSearchError(true)
      }
      setSearchInProgress(false)
    }

  }


  return <>
    <div id="article-analysis" />
    <Typography gutterBottom variant="h3" component="div" sx={{ color: '#003366', fontWeight: 'bold' }}>
      Article analysis
    </Typography>
    <Card variant="outlined" sx={{ pt: 4, mt: -1 }}>
      <CardContent>
        <form id="risk-score-search" noValidate onSubmit={handleSubmit}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <TextField fullWidth label="Search by DOI" variant="outlined" value={doiSearchQuery}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setDoiSearchQuery(event.target.value);
              }} error={invalidDoi} helperText="DOI can start with https://doi.org/ or you can input a partial doi. eg: /10.1155/2020/9879876"
            />
            <IconButton type="submit" aria-label="search" sx={{ ml: 2 }}>
              <SearchIcon sx={{ color: blue, fontSize: 40 }} />
            </IconButton>
          </Stack>
          {!inspireMe &&
            <Box sx={{ color: "rgba(0, 0, 0, 0.6)", p: 0, m: 2 }}>
              <Link onClick={() => setInspireMe(true)}>
                <Typography variant="body2" component="div" sx={{ mb: 1 }}>Looking for inspiration?</Typography>
              </Link>
            </Box>}
          {inspireMe &&
            <Box sx={{ color: "rgba(0, 0, 0, 0.6)", p: 0, m: 2 }}>
              <Typography variant="body2" component="div" sx={{ mb: 1 }}>Looking for inspiration? You can try the following DOIs:</Typography>
              <Link onClick={() => setDoiSearchQuery('https://doi.org/10.1155/2020/9879876')}><Typography variant="caption" component="div">https://doi.org/10.1155/2020/9879876</Typography></Link>
              <Link onClick={() => setDoiSearchQuery('https://doi.org/10.1080/10717544.2022.2048133')}><Typography variant="caption" component="div">https://doi.org/10.1080/10717544.2022.2048133</Typography></Link>
              <Link onClick={() => setDoiSearchQuery('https://doi.org/10.3389/fmats.2023.1218047')}><Typography variant="caption" component="div">https://doi.org/10.3389/fmats.2023.1218047</Typography></Link>
              <Link onClick={() => setDoiSearchQuery('https://doi.org/10.1038/s41598-020-66738-8')}><Typography variant="caption" component="div">https://doi.org/10.1038/s41598-020-66738-8</Typography></Link>
              <Link onClick={() => setDoiSearchQuery('https://doi.org/10.1016/j.urolonc.2020.05.021')}><Typography variant="caption" component="div">https://doi.org/10.1016/j.urolonc.2020.05.021</Typography></Link>
              <Link onClick={() => setDoiSearchQuery('https://doi.org/10.12790/ahm.20.0018')}><Typography variant="caption" component="div">https://doi.org/10.12790/ahm.20.0018</Typography></Link>
            </Box>}
          {searchInProgress &&
            <LinearProgress />}
          {doiNotFound &&
            <Typography variant="body2" style={{ marginTop: 40 }} color="error">Unable to find DOI. There are several reasons for this:
              <ul>
                <li>The article was published before Jan 2014. We only analyse articles from this date.</li>
                <li>The article has been very recently published and we don't have the data yet.</li>
                <li>The DOI does not exist.</li>
              </ul>
            </Typography>
          }
          {doiSearchError &&
            <Typography variant="body2" style={{ marginTop: 40 }} color="error">
              Unable to search by DOI. Something went wrong on our side, we are working to fix it. Please retry later.
            </Typography>
          }
        </form>
        {doiSearchResult &&
          <Fragment>
            <BootstrapDialog
              onClose={handleClose}
              aria-labelledby="customized-dialog-title"
              open={open}
              maxWidth="xl"
            >
              <DialogTitle sx={{ m: 0, p: 2, pr: 4, fontSize: { sm: '1em', md: '2em', xl: '3em' } }} id="customized-dialog-title">
                <ArticleTitle title={doiSearchResult.title} />
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],

                }}
              >
                <CloseIcon />
              </IconButton>
              <DialogContent dividers>
                <RiskScorePanel data={doiSearchResult} problematicReferences={problematicReferences} newRetractions={[]} />
              </DialogContent>
              <DialogActions>
                <Button autoFocus onClick={handleClose}>
                  Close
                </Button>
              </DialogActions>
            </BootstrapDialog>
          </Fragment>
        }        
      </CardContent></Card>
      <Snackbar
          open={openError}
          autoHideDuration={5000}
          onClose={handleCloseError}
          message="Sorry, we are unable to retrieve all the data for this analysis. Partial information might be shown."
        />
  </>
}

export default RiskScore;